<template>
  <div class="streaming-sections">
    <div
      v-for="section in orderedSections"
      :key="section"
      class="section-chunk"
    >
      <button
        class="section-title"
        :class="{ active: openSection === section }"
        @click="toggleSection(section)"
      >
        <SectionTitle :section="section" />
        <span class="arrow" :class="{ open: openSection === section }"
          >&#9660;</span
        >
      </button>
      <div v-if="openSection === section" class="section-content">
        <MarkDownComponent
          :message="{
            text: sectionResponses[section],
            isBot: true,
            sources: sectionSources[section] || []
          }"
        />
        <ConversationActions
          @action="
            (action: 'copy' | 'insert') => onSectionIconClick(action, section)
          "
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, type PropType } from 'vue';
import MarkDownComponent from '@/components/common/mark-down/MarkDownComponent.vue';
import ConversationActions from '@/components/common/conversation-actions/ConversationActions.vue';
import SectionTitle from '@/components/common/section-title/sectionTitle.vue';
import { SourceDTO } from '@/dto/QuestionResponseDTO';
import { useI18n } from 'vue-i18n';
import { useToasterStore } from '@/stores/useToasterStore';
import { OfficeUtils } from '@/utils/office.utils';

const props = defineProps({
  showSections: { type: Boolean, default: false },
  orderedSections: { type: Array as PropType<string[]> },
  sectionResponses: {
    type: Object as PropType<Record<string, string>>,
    required: true
  },
  sectionSources: {
    type: Object as PropType<Record<string, SourceDTO[]>>,
    default: () => ({})
  }
});

const { t } = useI18n();
const toaster = useToasterStore();
const openSection = ref<string | null>(null);

function toggleSection(section: string) {
  openSection.value = openSection.value === section ? null : section;
}

async function onSectionIconClick(
  action: 'copy' | 'insert',
  section: string
): Promise<void> {
  const text = props.sectionResponses[section];
  if (!text) return;
  try {
    if (action === 'copy') {
      await OfficeUtils.copyTextToClipboard(text);
      toaster.success({ title: t('actions.toast.text_copied') });
    } else if (action === 'insert') {
      await OfficeUtils.insertTextToDocument(text);
      toaster.success({ title: t('actions.toast.text_inserted') });
    }
  } catch {
    toaster.error({
      title:
        action === 'copy'
          ? t('actions.toast.text_copied.error')
          : t('actions.toast.text_inserted.error')
    });
  }
}
</script>

<style scoped lang="scss">
.streaming-sections {
  margin-top: $space-3;
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.section-chunk {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.section-title {
  position: relative;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.6rem 0.9rem;
  padding-left: 1.4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.9rem;
  font-weight: 400;
  color: #444;
}

.section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #1a73e8;
}

.section-chunk:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.arrow {
  font-size: 0.8rem;
  opacity: 0.7;
  transition: transform 0.2s ease;
}

.arrow.open {
  transform: rotate(180deg);
}

.section-title.active {
  font-weight: 700;
  color: #222;
}

.section-content {
  border-top: 1px solid #f0f0f0;
  padding: 0.6rem 0.9rem 0.8rem 1.4rem;
  font-size: 0.85rem;
  color: #333;
}

.section-content .markdown-content {
  font-size: 1rem;
  color: #222;
}

.card-actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-top: $space-3;
  gap: $space-3;
}
</style>
